import React from 'react';
import type { TpPatternDeclined, TpPatternWorked } from '../../patient-treatment/types';
import { Icon } from '../Icon/Icon';
import { PatientProfileTabSectionHeader } from './PatientProfileTabSectionHeader';

function PatternRow({
  date,
  primary,
  secondary,
  reason,
  first,
}: {
  date: string;
  primary: string;
  secondary: string;
  reason?: boolean;
  first?: boolean;
}) {
  return (
    <div
      className="grid grid-cols-[74px_1fr] gap-2.5 py-2.5"
      style={{ borderTop: first ? 'none' : '1px solid var(--cream-2)' }}
    >
      <span className="tc-mono text-[11px] font-medium text-[var(--ink-3)]">{date}</span>
      <div>
        <div className="text-[13px] font-medium tracking-[-0.005em] text-[var(--ink-1)]">{primary}</div>
        <div
          className="mt-0.5 text-[11.5px] leading-[1.4]"
          style={{ color: reason ? 'var(--terracotta)' : 'var(--ink-3)' }}
        >
          {secondary}
        </div>
      </div>
    </div>
  );
}

export interface PatientTreatmentPatternsProps {
  worked: TpPatternWorked[];
  didntWork: TpPatternDeclined[];
  takeaway: string;
}

export function PatientTreatmentPatterns({ worked, didntWork, takeaway }: PatientTreatmentPatternsProps) {
  return (
    <section className="tc-rise tc-d5 mb-[30px]">
      <PatientProfileTabSectionHeader
        eyebrow="Historical patterns"
        title="What's worked, what hasn't"
        icon="refresh"
        accent="var(--ink-3)"
      />
      <div
        className="mb-3.5 grid gap-3.5"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}
      >
        <div className="tc-card px-5 py-4">
          <div className="mb-2 flex items-center gap-1.5">
            <Icon kind="check-circ" size={13} style={{ color: 'var(--success)' }} />
            <span className="tc-eyebrow text-[var(--success)]">
              Worked · {worked.length} accepted
            </span>
          </div>
          {worked.map((w, i) => (
            <PatternRow
              key={`${w.date}-${w.item}`}
              date={w.date}
              primary={w.item}
              secondary={w.note}
              first={i === 0}
            />
          ))}
          {worked.length === 0 ? (
            <p className="m-0 text-[13px] text-[var(--ink-3)]">No accepted patterns on file yet.</p>
          ) : null}
        </div>
        <div className="tc-card px-5 py-4">
          <div className="mb-2 flex items-center gap-1.5">
            <Icon kind="x" size={13} stroke={2.2} style={{ color: 'var(--terracotta)' }} />
            <span className="tc-eyebrow text-[var(--terracotta)]">
              Didn&apos;t work · {didntWork.length} declined
            </span>
          </div>
          {didntWork.map((d, i) => (
            <PatternRow
              key={`${d.date}-${d.item}`}
              date={d.date}
              primary={d.item}
              secondary={d.reason}
              reason
              first={i === 0}
            />
          ))}
          {didntWork.length === 0 ? (
            <p className="m-0 text-[13px] text-[var(--ink-3)]">No declines logged yet.</p>
          ) : null}
        </div>
      </div>
      <div className="flex items-start gap-2.5 rounded-[var(--r-md)] border border-[color-mix(in_srgb,var(--ai-3)_18%,transparent)] bg-[color-mix(in_srgb,var(--ai-3)_6%,var(--cream-1))] px-[18px] py-3.5">
        <Icon kind="sparkle" size={14} stroke={1.9} style={{ color: 'var(--ai-3)', marginTop: 2 }} />
        <div>
          <span className="tc-eyebrow mb-1 block text-[var(--ai-3)]">Pattern</span>
          <p className="m-0 text-[13px] leading-[1.6] tracking-[-0.005em] text-[var(--ink-1)]">
            {takeaway}
          </p>
        </div>
      </div>
    </section>
  );
}
